import React, { useState, useEffect } from "react";
import { auth } from "./firebaseConfig";
import { useNavigate } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const LoadingSpinner = () => (
  <div className="fixed inset-0 flex justify-center items-center bg-white z-50">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-500"></div>
  </div>
);

const RecipePage = ({ recipes, syrups }) => {
  const [user, setUser] = useState(null);
  const [delayDone, setDelayDone] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedIngredients, setSelectedIngredients] = useState(new Set());
  const [completedSteps, setCompletedSteps] = useState([]);


  const navigate = useNavigate();

  // Simulate loading delay
  useEffect(() => {
    const timer = setTimeout(() => setDelayDone(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auth logic
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser?.email === "tifsubiscuit@gmail.com") {
        setUser(currentUser);
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // ✅ Access Denied UI for unauthorized users
  if (!user && delayDone) {
    return (
      <div className="fixed inset-0 flex justify-center items-center bg-white z-50">
        <div className="bg-white border border-gray-300 shadow-lg rounded-lg p-8 max-w-sm w-full text-center">
          <div className="flex justify-center mb-4">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mb-4" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">Access Denied</h2>
          <p className="text-gray-600 mb-6">You do not have the required permissions to view this page.</p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ✅ Loading spinner (until user data + delay is complete)
  if (!delayDone || !user) {
    return <LoadingSpinner />;
  }

  // ✅ Ingredient selection toggle
  const handleIngredientClick = (ingredientName) => {
    setSelectedIngredients((prevSelected) => {
      const newSelection = new Set(prevSelected);
      if (newSelection.has(ingredientName)) {
        newSelection.delete(ingredientName);
      } else {
        newSelection.add(ingredientName);
      }
      return newSelection;
    });
  };

  const selectedData = selectedCategory === "cookies" ? recipes : syrups;

  const generateSteps = (ingredients) => {
    const steps = [];
    Object.entries(ingredients).forEach(([category, items]) => {
      Object.entries(items).forEach(([name, data]) => {
        if (typeof data === "object" && data.amount !== undefined) {
          steps.push(`Measure ${data.amount}${data.unit ? ' ' + data.unit : ''} of ${name}`);
        } else {
          steps.push(`Measure ${data}g of ${name}`); // fallback for shorthand
        }
      });
    });
    return steps;
  };

  const recipeSteps = selectedRecipe ? generateSteps(selectedData[selectedRecipe].ingredients) : [];

  const generateInstructionSteps = (ingredients) => {
    const steps = [];

    if (ingredients.Sugars || ingredients.Eggs) {
      steps.push("🥣 Wet Ingredients:");
      if (ingredients.Sugars) {
        Object.entries(ingredients.Sugars).forEach(([name, value]) => {
          const { amount, unit = "g" } = typeof value === "object" ? value : { amount: value, unit: "g" };
          steps.push(`- Add ${amount} ${unit} of ${name} to mixing bowl.`);
        });
      }
      if (ingredients.Eggs) {
        Object.entries(ingredients.Eggs).forEach(([name, value]) => {
          const { amount, unit = "x" } = typeof value === "object" ? value : { amount: value, unit: "x" };
          steps.push(`- Crack ${amount} ${unit} of ${name} into the bowl.`);
        });
      }
    }

    if (ingredients.Flour || ingredients.Chocolate || ingredients.Nuts) {
      steps.push("🧁 Dry Ingredients:");
      if (ingredients.Flour) {
        Object.entries(ingredients.Flour).forEach(([name, value]) => {
          const { amount, unit = "g" } = typeof value === "object" ? value : { amount: value, unit: "g" };
          steps.push(`- Sift in ${amount} ${unit} of ${name}.`);
        });
      }
      if (ingredients.Chocolate) {
        Object.entries(ingredients.Chocolate).forEach(([name, value]) => {
          const { amount, unit = "g" } = typeof value === "object" ? value : { amount: value, unit: "g" };
          steps.push(`- Fold in ${amount} ${unit} of ${name}.`);
        });
      }
      if (ingredients.Nuts) {
        Object.entries(ingredients.Nuts).forEach(([name, value]) => {
          const { amount, unit = "g" } = typeof value === "object" ? value : { amount: value, unit: "g" };
          steps.push(`- Add ${amount} ${unit} of ${name}.`);
        });
      }
    }

    steps.push("🌀 Rolling & Finishing:");
    steps.push("- Mix everything until combined.");
    steps.push("- Portion the dough into balls.");
    steps.push("- Place on a tray and bake.");

    return steps;
  };



  // ✅ No category selected - show categories
  if (!selectedCategory) {
    return (
      <div className="container mx-auto p-4 text-center">
        <h1 className="text-3xl font-bold mb-6">Select Category</h1>
        <div className="grid grid-cols-2 gap-6">
          <button
            onClick={() => setSelectedCategory("cookies")}
            className="p-6 bg-blue-500 text-white rounded-lg text-xl"
          >
            Cookies
          </button>
          <button
            onClick={() => setSelectedCategory("syrups")}
            className="p-6 bg-yellow-500 text-white rounded-lg text-xl"
          >
            Syrups
          </button>
        </div>
      </div>
    );
  }

  // ✅ Show recipe list for selected category
  if (!selectedRecipe) {
    return (
      <div className="container mx-auto p-4">
        <button
          onClick={() => setSelectedCategory(null)}
          className="px-4 py-2 bg-gray-300 rounded-lg mb-4"
        >
          ← Back
        </button>
        <h1 className="text-3xl font-bold text-center mb-6">
          {selectedCategory === "cookies" ? "Cookies" : "Syrups"}
        </h1>
        <div className="grid grid-cols-4 gap-4">
          {Object.keys(selectedData).map((item) => (
            <div
              key={item}
              onClick={() => setSelectedRecipe(item)}
              className="cursor-pointer border rounded-lg p-4 text-center bg-white hover:bg-gray-100 shadow-md"
            >
              <img
                src={selectedData[item].image}
                alt={item}
                className="w-full h-32 object-contain mb-2"
              />
              <h3 className="text-lg font-bold">{item}</h3>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ✅ Show specific recipe details
  return (
    <div className="container mx-auto p-4">
      <button
        onClick={() => setSelectedRecipe(null)}
        className="px-4 py-2 bg-gray-300 rounded-lg mb-4"
      >
        ← Back
      </button>
      <h1 className="text-3xl font-bold text-center mb-6">{selectedRecipe}</h1>
      <p className="text-gray-600 text-center mb-4 italic">
        {selectedData[selectedRecipe].subtitle}
      </p>
      <img
        src={selectedData[selectedRecipe].image}
        alt={selectedRecipe}
        className="w-48 h-48 object-contain mx-auto mb-6"
      />

      <ul>
        {Object.entries(selectedData[selectedRecipe].ingredients).map(([category, items]) => (
          <li key={category} className="mb-6">
            <h3 className="text-xl font-bold text-blue-600 mb-3">{category}</h3>
            <ul className="pl-4">
              {Object.entries(items).map(([ingredient, { amount, unit }]) => (
                <li
                  key={ingredient}
                  onClick={() => handleIngredientClick(ingredient)}
                  className={`p-2 border-2 rounded-lg text-lg cursor-pointer transition-colors ${selectedIngredients.has(ingredient)
                    ? "bg-green-300"
                    : "bg-white hover:bg-gray-100"
                    }`}
                >
                  {ingredient}: {amount} {unit}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4">Steps</h2>
      <ol className="space-y-4">
        {recipeSteps.map((step, index) => (
          <li key={index} className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={completedSteps.includes(index)}
              onChange={() => {
                setCompletedSteps(prev =>
                  prev.includes(index)
                    ? prev.filter(i => i !== index)
                    : [...prev, index]
                );
              }}
              className="mt-1"
            />
            <span className={completedSteps.includes(index) ? "line-through text-gray-400" : ""}>
              Step {index + 1}: {step}
            </span>
          </li>
        ))}
      </ol>

      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4 text-[#4c5d99]">Step-by-Step Instructions</h2>
        <ul className="space-y-2 list-disc list-inside text-lg text-gray-800">
          {generateInstructionSteps(selectedData[selectedRecipe].ingredients).map((step, index) => (
            <li key={index} className="transition duration-200">
              {step}
            </li>
          ))}
        </ul>
      </div>


      {selectedCategory === "syrups" && (
        <p className="mt-6 text-lg font-semibold">
          Instructions: {selectedData[selectedRecipe].instructions}
        </p>
      )}
    </div>
  );
};

export default RecipePage;
